import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Cpu,
  MapPin,
  Download,
  PanelsTopLeft,
  Workflow,
} from 'lucide-react'
import { getCaseStudy } from '../../data/caseStudies'
import RobotPocket from '../RobotPocket'
import SiteFooter from '../SiteFooter'
import TechStack from '../TechStack'
import useHomeMotion from './useHomeMotion'
import './console.css'

const projects = [
  {
    slug: 'property-platform',
    label: 'Product',
    icon: PanelsTopLeft,
    name: 'Emmut',
    description: 'Led a four-developer team · 1,000+ units.',
    metric: '3h → 1h',
    metricCaption: 'Daily admin work per employee. Measured.',
  },
  {
    slug: 'workflow-migration',
    label: 'Cloud',
    icon: Workflow,
    name: 'AWS → Railway',
    description: 'Moved lead intake from Kestra to n8n.',
    metric: '7 workflows',
    metricCaption: 'Production cutover September 2026.',
  },
  {
    slug: 'local-inference',
    label: 'Systems',
    icon: Cpu,
    name: 'Local inference',
    description: 'ExLlamaV3 & TabbyAPI.',
    metric: '2 merged fixes',
    metricCaption: 'Historical six-GPU cold prompts: +18%; decode unchanged.',
  },
]

export default function Home() {
  const [selected, setSelected] = useState(0)
  const root = useRef(null)
  useHomeMotion(root, selected)
  const project = projects[selected]
  const study = getCaseStudy(project.slug)
  const cover = study.cover || study.visual

  return (
    <div
      className="orbit-home"
      ref={root}
      role="region"
      aria-label="Digital Diego portfolio"
    >
      <section className="home-hero" aria-label="Introduction">
        <div className="home-intro">
          <p className="home-eyebrow">Product. Cloud. Infrastructure.</p>
          <h1>
            Hi, I’m <span className="home-name">Diego.</span>
          </h1>
          <p className="home-role">Full-stack engineer & technical lead.</p>
          <p className="home-description">
            I build web products, lead teams, and run the systems behind them.
          </p>
          <TechStack
            items={['React', 'TypeScript', 'Go', 'Python', 'AWS']}
            label="Core technologies"
            className="home-tech-stack"
            compact
          />
          <div className="home-actions">
            <Link className="button" to="/portfolio">
              View selected work <ArrowUpRight size={19} aria-hidden="true" />
            </Link>
            <Link className="button button-secondary" to="/contact">
              Let’s talk <ArrowRight size={19} aria-hidden="true" />
            </Link>
          </div>
          <p className="home-availability">
            <span className="home-status-dot" aria-hidden="true" />
            Open to senior & lead roles
            <span className="home-location">
              <MapPin size={14} aria-hidden="true" /> Dallas, TX
            </span>
          </p>
        </div>
        <section className="home-workbench" aria-labelledby="home-work-title">
          <div className="home-work-heading">
            <h2 id="home-work-title">A few things I’ve shipped.</h2>
            <Link to="/portfolio">
              All projects <ArrowUpRight size={17} aria-hidden="true" />
            </Link>
          </div>
          <div className="home-work-grid">
            {projects.map((item) => (
              <article
                className="home-work-summary"
                key={item.slug}
                aria-labelledby={`home-${item.slug}-title`}
              >
                <h3 id={`home-${item.slug}-title`}>
                  <Link
                    to={`/work/${item.slug}`}
                    aria-label={`Open ${item.name} case study`}
                  >
                    {item.name}
                    <ArrowUpRight size={17} aria-hidden="true" />
                  </Link>
                </h3>
                <p className="home-summary-result">{item.metric}</p>
                <p className="home-summary-description">
                  {item.description}{' '}
                  <span className="home-summary-scope">
                    {item.metricCaption}
                  </span>
                </p>
              </article>
            ))}
          </div>
        </section>
        <div
          className="home-playground"
          role="region"
          aria-label="Selected work preview"
        >
          <div className="home-stage-top">
            <span>
              Selected work <ArrowDownRight size={18} aria-hidden="true" />
            </span>
            <span className="home-stage-note">{project.name}</span>
          </div>
          <figure className="home-cover" data-cover={Boolean(study.cover)}>
            <Link
              className="home-cover-picture"
              to={`/work/${project.slug}`}
              aria-label={`Preview ${project.name} case study`}
            >
              <img
                key={project.slug}
                src={cover.src}
                alt={cover.alt}
                width="1280"
                height="800"
                decoding="async"
              />
            </Link>
            <figcaption>
              <span>{cover.caption}</span>
              <RobotPocket selected={selected} />
            </figcaption>
          </figure>
          <div
            className="home-scenes"
            role="group"
            aria-label="Choose a project"
          >
            {projects.map((item, index) => (
              <button
                key={item.slug}
                type="button"
                aria-pressed={index === selected}
                aria-controls="home-preview-readout"
                onClick={() => setSelected(index)}
              >
                <item.icon size={17} strokeWidth={1.6} aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
          <div
            id="home-preview-readout"
            className="home-project-content"
            aria-live="polite"
            aria-atomic="true"
          >
            <Link
              to={`/work/${project.slug}`}
              aria-label={`View selected ${project.name} case study`}
            >
              Explore {project.name}{' '}
              <ArrowUpRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
      <div className="home-links">
        <Link className="home-lab-link" to="/infrastructure">
          <Cpu size={22} aria-hidden="true" />
          <span>
            <small>Built at home</small>
            <strong>Seven GPUs. Built and run by me.</strong>
          </span>
          <ArrowUpRight size={21} aria-hidden="true" />
        </Link>
        <Link className="home-agent-link" to="/infrastructure#agents">
          <Workflow size={22} aria-hidden="true" />
          <span>
            <small>Agent environments, connected tools & local inference</small>
            <strong>Hermes, agents & MCP</strong>
          </span>
          <ArrowUpRight size={21} aria-hidden="true" />
        </Link>
        <a
          className="home-resume-link"
          href={`${process.env.PUBLIC_URL || ''}/resume/Diego_Espino_Resume.pdf`}
          download
        >
          <Download size={22} aria-hidden="true" />
          <span>
            <small>One-page PDF</small>
            <strong>Download resume</strong>
          </span>
          <ArrowUpRight size={21} aria-hidden="true" />
        </a>
      </div>
      <SiteFooter />
    </div>
  )
}
